"use client";

import * as React from "react";
import { Auth0Provider } from "@auth0/auth0-react";
import { ClerkProvider } from "@clerk/clerk-react";
import { Helmet, HelmetProvider } from "react-helmet-async";

import "@/styles/global.css";

import type { Metadata } from "@/types/metadata";
import { appConfig } from "@/config/app";
import { AuthStrategy } from "@/lib/auth-strategy";
import { providerProps as auth0ProviderProps } from "@/lib/auth0/config";
import { providerProps as clerkProviderProps } from "@/lib/clerk/config";
import { getSettings as getPersistedSettings } from "@/lib/settings";
import { AuthProvider as CognitoProvider } from "@/components/auth/cognito/auth-context";
import { AuthProvider as CustomAuthProvider } from "@/components/auth/custom/auth-context";
import { AuthProvider as SupabaseProvider } from "@/components/auth/supabase/auth-context";
import { ThemeProvider } from "@/components/core/theme-provider";
import { Analytics } from "@/components/core/analytics";
import { I18nProvider } from "@/components/core/i18n-provider";
import { Rtl } from "@/components/core/rtl";
import { SettingsProvider } from "@/components/core/settings/settings-context";
import { Toaster } from "@/components/core/toaster";
import { QueryClient, HydrationBoundary } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import { dayjs } from "./lib/dayjs";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { WorkspaceProvider } from "./components/scadadashboard/layout/vertical/workspace-context";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

const metadata = { title: appConfig.name } satisfies Metadata;

let AuthProvider: React.FC<React.PropsWithChildren> = React.Fragment;

if (appConfig.authStrategy === AuthStrategy.AUTH0) {
    AuthProvider = function AuthProvider(props: React.PropsWithChildren): React.JSX.Element {
        return <Auth0Provider {...auth0ProviderProps} {...props} />;
    };
}

if (appConfig.authStrategy === AuthStrategy.CLERK) {
    AuthProvider = function AuthProvider(props: React.PropsWithChildren) {
        return <ClerkProvider {...clerkProviderProps} {...props} />;
    };
}

if (appConfig.authStrategy === AuthStrategy.COGNITO) {
    AuthProvider = CognitoProvider as React.FC<React.PropsWithChildren>;
}

if (appConfig.authStrategy === AuthStrategy.CUSTOM) {
    AuthProvider = CustomAuthProvider as React.FC<React.PropsWithChildren>;
}

if (appConfig.authStrategy === AuthStrategy.SUPABASE) {
    AuthProvider = SupabaseProvider as React.FC<React.PropsWithChildren>;
}

export interface RootProps {
    children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
    const settings = getPersistedSettings();
    const APP_VERSION = import.meta.env.VITE_BUILD_NUMBER ?? "dev";

    dayjs.locale("de");

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        gcTime: 1000 * 60 * 5,
                        staleTime: 0,
                        refetchOnWindowFocus: false,
                        retry: 1,
                    },
                },
            })
    );

    //const persister = React.useMemo(() => {
    //    return createSyncStoragePersister({
    //        storage: globalThis.localStorage,
    //        key: "SCADA_REACT_QUERY_CACHE",
    //        throttleTime: 5000,
    //    });
    //}, []);

    const persister = React.useMemo(() => {
        return createAsyncStoragePersister({
            storage: globalThis.localStorage,
            key: "SCADA_REACT_QUERY_CACHE",
            throttleTime: 5000,
        });
    }, []);

    return (
        <HelmetProvider>
            <Helmet>
                <title>{metadata.title}</title>
                <meta content={appConfig.themeColor} name="theme-color" />
            </Helmet>

            <AuthProvider>
                <Analytics>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                        <SettingsProvider settings={settings}>
                            <I18nProvider>
                                <Rtl>
                                    <PersistQueryClientProvider
                                        client={queryClient}
                                        persistOptions={{
                                            persister,
                                            maxAge: 1000 * 60 * 60 * 24,
                                            buster: APP_VERSION,
                                            dehydrateOptions: {
                                                shouldDehydrateQuery: (query) => {
                                                    return query.meta?.persist === true && query.state.status === "success";
                                                },
                                            },
                                        }}
                                    >
                                        <HydrationBoundary state={undefined} queryClient={queryClient}>
                                            <ThemeProvider>
                                                <WorkspaceProvider>{children}</WorkspaceProvider>
                                                <Toaster position="bottom-right" />
                                            </ThemeProvider>
                                        </HydrationBoundary>
                                    </PersistQueryClientProvider>
                                </Rtl>
                            </I18nProvider>
                        </SettingsProvider>
                    </LocalizationProvider>
                </Analytics>
            </AuthProvider>
        </HelmetProvider>
    );
}
